import type {
  SocialExtractionStatus,
  SocialFindingSeverity,
  SocialJudgment,
} from "contracts/enums/social";
import {
  SOCIAL_EXTRACTION_STATUS,
  SOCIAL_FINDING_SEVERITY,
  SOCIAL_JUDGMENT,
} from "contracts/enums/social";

const SOCIAL_EXTRACTION_PHASES = [
  "in_progress",
  "failed",
  "attention",
  "settled",
] as const;
type SocialExtractionPhase = (typeof SOCIAL_EXTRACTION_PHASES)[number];
export const SOCIAL_EXTRACTION_PHASE = {
  IN_PROGRESS: "in_progress",
  FAILED: "failed",
  ATTENTION: "attention",
  SETTLED: "settled",
} as const satisfies Record<string, SocialExtractionPhase>;

/** How the report treats each stored extraction status. Exhaustive, so a new
 * status fails to compile until this surface decides what it means here. */
export const EXTRACTION_PHASE_BY_STATUS: Record<
  SocialExtractionStatus,
  SocialExtractionPhase
> = {
  [SOCIAL_EXTRACTION_STATUS.QUEUED]: SOCIAL_EXTRACTION_PHASE.IN_PROGRESS,
  [SOCIAL_EXTRACTION_STATUS.STARTING]: SOCIAL_EXTRACTION_PHASE.IN_PROGRESS,
  [SOCIAL_EXTRACTION_STATUS.RUNNING]: SOCIAL_EXTRACTION_PHASE.IN_PROGRESS,
  [SOCIAL_EXTRACTION_STATUS.IMPORTING]: SOCIAL_EXTRACTION_PHASE.IN_PROGRESS,
  [SOCIAL_EXTRACTION_STATUS.STOPPING]: SOCIAL_EXTRACTION_PHASE.IN_PROGRESS,
  [SOCIAL_EXTRACTION_STATUS.STOPPED]: SOCIAL_EXTRACTION_PHASE.SETTLED,
  [SOCIAL_EXTRACTION_STATUS.COMPLETED]: SOCIAL_EXTRACTION_PHASE.SETTLED,
  [SOCIAL_EXTRACTION_STATUS.FAILED]: SOCIAL_EXTRACTION_PHASE.FAILED,
  [SOCIAL_EXTRACTION_STATUS.START_AMBIGUOUS]: SOCIAL_EXTRACTION_PHASE.ATTENTION,
};

export const REPORT_EXTRACTION_STATUS_LABELS: Record<
  SocialExtractionStatus,
  string
> = {
  [SOCIAL_EXTRACTION_STATUS.QUEUED]: "Collection queued",
  [SOCIAL_EXTRACTION_STATUS.STARTING]: "Collection starting",
  [SOCIAL_EXTRACTION_STATUS.RUNNING]: "Collecting posts",
  [SOCIAL_EXTRACTION_STATUS.IMPORTING]: "Saving collected posts",
  [SOCIAL_EXTRACTION_STATUS.STOPPING]: "Collection stopping",
  [SOCIAL_EXTRACTION_STATUS.STOPPED]: "Collection stopped",
  [SOCIAL_EXTRACTION_STATUS.COMPLETED]: "Up to date",
  [SOCIAL_EXTRACTION_STATUS.FAILED]: "Collection failed",
  [SOCIAL_EXTRACTION_STATUS.START_AMBIGUOUS]: "Collection needs attention",
};

export const REPORT_JUDGMENT_LABELS: Record<SocialJudgment, string> = {
  [SOCIAL_JUDGMENT.PASS]: "Passed",
  [SOCIAL_JUDGMENT.FAIL]: "Failed",
  [SOCIAL_JUDGMENT.NOT_APPLICABLE]: "Not applicable",
  [SOCIAL_JUDGMENT.UNABLE_TO_VERIFY]: "Could not be verified",
};

export const REPORT_SEVERITY_LABELS: Record<SocialFindingSeverity, string> = {
  [SOCIAL_FINDING_SEVERITY.LOW]: "Low",
  [SOCIAL_FINDING_SEVERITY.MEDIUM]: "Medium",
  [SOCIAL_FINDING_SEVERITY.HIGH]: "High",
};

export const POST_ANALYSIS_STATES = [
  "pending",
  "running",
  "failed",
  "clean",
  "flagged",
] as const;
export type PostAnalysisStateKind = (typeof POST_ANALYSIS_STATES)[number];
export const POST_ANALYSIS_STATE = {
  PENDING: "pending",
  RUNNING: "running",
  FAILED: "failed",
  CLEAN: "clean",
  FLAGGED: "flagged",
} as const satisfies Record<string, PostAnalysisStateKind>;

export const POST_ANALYSIS_STATE_LABELS: Record<PostAnalysisStateKind, string> =
  {
    [POST_ANALYSIS_STATE.PENDING]: "Analysis pending",
    [POST_ANALYSIS_STATE.RUNNING]: "Analysis in progress",
    [POST_ANALYSIS_STATE.FAILED]: "Analysis failed",
    [POST_ANALYSIS_STATE.CLEAN]: "Analysed — no issues found",
    [POST_ANALYSIS_STATE.FLAGGED]: "Analysed",
  };
